@use 'sass:map';
@use '../../scss/global' as *;
@use 'themes';

// ================================================================ //
// -- Local Variables

$sidebar-closed-width: 32px;

$toggle-offset-top: 8px;
$toggle-transition-speed: map.get($transition-durations, rapid);

// ================================================================ //
// -- Component Styles

:host {
  flex-grow: 0;
  flex-shrink: 1;
  display: flex;
  position: relative;

  .fluid-sidebar {
    width: $sidebar-closed-width;
    position: relative;

    @include transition($properties: 'width');

    // == Open Sidebar (Desktop)

    &.sidebar-open-desktop {
      width: var(--sidebar-open-width);
    }

    // == Open/Close Transition State

    &.transition-state {
      will-change: width;
    }

    // ==== Body Container

    .sidebar-body {
      width: var(--sidebar-open-width);
      max-width: 100vw;
      height: 100%;
      display: flex;
      flex-direction: column;
      position: absolute;
      background-color: var(--body-background-color);
      border-width: 0;
      border-style: solid;
      border-color: var(--body-border-color);
      color: var(--body-text-color, inherit); // Fall back to "inherit" when variable is undefined
      contain: size layout style;

      @include transition($properties: 'transform, box-shadow');

      @include screen-lg {
        width: 100%;
        position: relative;
        z-index: map.get($stacking-context, sidebar-body);
      }

      // == Fixed Position

      &.sidebar-fixed {
        max-height: 100vh;
        position: sticky;
        top: 0;
      }

      // == Left Sidebar

      &.sidebar-left {
        border-right-width: var(--G-border-width);

        @include screen-lg-down {
          left: 0;
          transform: translateX(calc(-100% + $sidebar-closed-width));
          contain: strict;

          // == Open Sidebar (Mobile)

          &.sidebar-open-mobile {
            transform: translateX(0);
            box-shadow: var(--box-shadow);
            contain: size layout style;
          }
        }
      }

      // == Right Sidebar

      &.sidebar-right {
        border-left-width: var(--G-border-width);

        @include screen-lg-down {
          left: 0;
          contain: strict;

          // == Open Sidebar (Mobile)

          &.sidebar-open-mobile {
            transform: translateX(calc(-100% + $sidebar-closed-width));
            box-shadow: var(--box-shadow);
            contain: size layout style;
          }
        }
      }

      // == Open/Close Transition State

      &.transition-state {
        overflow: clip;
      }
    }

    // ==== Content Container

    .sidebar-content {
      width: 100%;
      height: 100%;
      overflow-y: auto;
      overflow-x: clip;
      padding-top: var(--sidebar-padding-top);
      padding-bottom: var(--sidebar-padding-bottom);
      padding-left: var(--sidebar-padding-left);
      padding-right: var(--sidebar-padding-right);
      scrollbar-color: var(--scrollbar-color, auto);
      contain: size style;

      // Fix "upward expansion" issue in Chrome / Edge
      &:has(fluid-tree-menu) {
        scroll-padding-block: 25vh; // Applies to Tree Menu "auto scroll"
        overflow-anchor: none;
      }

      // == Fade In (Open Sidebar)

      &.content-visible {
        @include fade-in(medium);
      }

      // == Fade Out (Closed Sidebar)

      &.content-hidden {
        @include fade-out(medium);
        overflow: clip;
      }

      // == Open/Close Transition State

      &.transition-state {
        // Prevents layout shift when Sidebar opens/closes
        width: calc(var(--sidebar-open-width) - 1px);
      }
    }

    // ==== Toggle Button Container

    .sidebar-toggle-container {
      width: var(--sidebar-open-width);
      max-width: calc(100vw - (var(--toggle-btn-size) / 2)); // Keep toggle within mobile viewport
      height: calc(var(--toggle-btn-size) + $toggle-offset-top);
      position: sticky;
      top: 0;
      padding-top: $toggle-offset-top;
      margin-bottom: calc((var(--toggle-btn-size) + $toggle-offset-top) * -1); // Pull body content up
      pointer-events: none; // Prevent container from blocking access to menu items
      z-index: map.get($stacking-context, sidebar-toggle-container);

      // Delay change in appearance until Sidebar finishes opening/closing
      transition:
        width 0ms $toggle-transition-speed, // Delay
        height 0ms $toggle-transition-speed, // Delay
        transform 0ms $toggle-transition-speed, // Delay
        padding 0ms $toggle-transition-speed, // Delay
        margin 0ms $toggle-transition-speed; // Delay

      @include screen-lg {
        width: 100%;
      }

      // == Left Sidebar

      &.toggle-left {
        text-align: right;
      }

      // == Right Sidebar

      &.toggle-right {
        text-align: left;
        transform: translateX(calc(-100% + $sidebar-closed-width));

        @include screen-lg {
          transform: none;
        }
      }

      // == Full-Height Container (closed Sidebar)

      &.expand-toggle-container {
        width: 100%;
        height: 100%;
        position: absolute;
        transform: none;
        padding: 0;
        margin: 0;
        pointer-events: unset;
        z-index: map.get($stacking-context, sidebar-body) + 1;
      }
    }

    // ==== Open/Close Toggle Button

    .sidebar-toggle-left,
    .sidebar-toggle-right {
      @include button-reset;
      width: var(--toggle-btn-size);
      height: var(--toggle-btn-size);
      max-height: 100vh; // Full-height toggle button
      display: inline-flex;
      justify-content: center;
      align-items: center;
      position: relative;
      color: var(--toggle-btn-icon-color); // Sets Icon "fill" property
      background-color: var(--toggle-btn-background-color);
      border-width: var(--G-border-width);
      border-style: solid;
      border-color: var(--body-border-color);
      border-radius: var(--toggle-btn-border-radius);
      pointer-events: auto;

      // Delay change in appearance until Sidebar finishes opening/closing
      transition:
        background-color map.get($transition-durations, short) 0ms,
        width 0ms $toggle-transition-speed, // Delay
        height 0ms $toggle-transition-speed, // Delay
        transform 0ms $toggle-transition-speed, // Delay
        color 0ms $toggle-transition-speed, // Delay
        border-radius 0ms $toggle-transition-speed; // Delay

      // == Hover, Focus-Visible, & Active States

      @mixin toggle-active-state {
        background-color: var(--toggle-btn-background-color-active);

        .toggle-icon {
          opacity: 1;
        }
      }

      &:focus-visible,
      &:active {
        @include toggle-active-state;
      }

      // Prevent "sticky hover state" on touch devices
      @include input-device-pointer {
        &:hover {
          @include toggle-active-state;
        }
      }

      // == Fade Out (during Sidebar open/close transition)

      &.transition-state {
        animation: fadeOut $toggle-transition-speed ease-out forwards;
      }

      // == Fade In (when Sidebar finishes opening/closing)

      &:not(.transition-state) {
        animation: fadeIn $toggle-transition-speed ease-in forwards;
      }

      // == Full-Height Toggle Button (closed Sidebar)

      &.expand-toggle {
        width: 100%;
        height: 100%;
        position: sticky;
        top: 0;
        border-radius: unset;
        margin: 0;
      }
    }

    // ==== Left Sidebar Toggle

    .sidebar-toggle-left {
      //noinspection CssInvalidFunction
      transform: translateX(calc(var(--toggle-btn-size) / 2));

      // == Full-Height Toggle Button (Closed Sidebar)

      &.expand-toggle {
        transform: none;
        border-width: 0 var(--G-border-width) 0 0;
      }
    }

    // ==== Right Sidebar Toggle

    .sidebar-toggle-right {
      //noinspection CssInvalidFunction
      transform: translateX(calc(var(--toggle-btn-size) / -2));

      // == Full-Height Toggle Button (Closed Sidebar)

      &.expand-toggle {
        transform: none;
        border-width: 0 0 0 var(--G-border-width);
      }
    }

    // ==== Arrow Icon

    .toggle-icon {
      position: relative;
      opacity: 0.6;

      @include transition(
        $properties: 'opacity',
        $duration: short
      );
    }
  }
}
